import React, { useEffect, useState } from "react";
import {
  Building,
  Users,
  FileText,
  CreditCard,
  TrendingUp,
  AlertTriangle,
} from "lucide-react";
import { DashboardCharts } from "../../components/analytics/DashboardCharts";
import { DashboardService } from "../../services/analytics/dashboardService";
import { AnalyticsService } from "../../services/analytics/analyticsService";
import { toast } from "react-hot-toast";
import { useSelector } from "react-redux";
import { RootState } from "../../store";
import { getUserDisplayName } from "../../utils/userUtils";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

interface Alert {
  id: string | number;
  type: "warning" | "error" | "info";
  title: string;
  description: string;
  time: string;
}

export const AdminDashboard: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useSelector((state: RootState) => state.auth);
  const [stats, setStats] = useState<any>(null);
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const data = await DashboardService.getAdminStats();
        setStats(data);
      } catch (error) {
        console.error("Error fetching admin stats", error);
        toast.error(t('common.errorGroup'));
      } finally {
        setLoading(false);
      }
    };

    const fetchAlerts = async () => {
      try {
        const res = await AnalyticsService.getAlerts();
        setAlerts(res.data?.results ?? []);
      } catch (error) {
        console.error("Error fetching alerts", error);
      }
    };

    fetchStats();
    fetchAlerts();
  }, []);

  const adminStats = [
    {
      name: t('dashboard.stats.activeClients'),
      value: stats?.clients?.actifs?.toString() || "0",
      change: stats ? `${stats.clients.total} ${t('dashboard.stats.total')}` : "-",
      changeType: "neutral",
      icon: Building,
      color: "blue",
    },
    {
      name: t('dashboard.stats.suppliers'),
      value: stats?.fournisseurs?.total?.toString() || "0",
      change: "+0", // No history yet
      changeType: "neutral",
      icon: Users,
      color: "green",
    },
    {
      name: t('dashboard.stats.totalInvoices'),
      value: stats?.factures?.total?.toString() || "0",
      change: stats ? `${stats.factures.en_attente} ${t('dashboard.stats.pending')}` : "-",
      changeType: "increase",
      icon: FileText,
      color: "purple",
    },
    {
      name: t('dashboard.stats.totalVolume'),
      value: stats?.factures?.montant_total 
        ? new Intl.NumberFormat(t('i18next.language') === 'fr' ? 'fr-FR' : 'en-US', { notation: "compact", compactDisplay: "short", style: "currency", currency: "XOF" }).format(stats.factures.montant_total)
        : "0 FCFA",
      change: "+0%",
      changeType: "increase",
      icon: TrendingUp,
      color: "yellow",
    },
    {
      name: t('dashboard.stats.completedPayments'),
      value: stats?.paiements?.total?.toString() || "0",
      change: stats ? new Intl.NumberFormat(t('i18next.language') === 'fr' ? 'fr-FR' : 'en-US', { notation: "compact", compactDisplay: "short", style: "currency", currency: "XOF" }).format(stats.paiements.montant_total) : "-",
      changeType: "increase",
      icon: CreditCard,
      color: "blue", // Changed from red as it's payment count, not pending
    },
    {
      name: t('dashboard.stats.alerts'),
      value: alerts.length.toString(),
      change: alerts.filter((a) => a.type === "error").length.toString(),
      changeType: "neutral",
      icon: AlertTriangle,
      color: "orange",
    },
  ];

  if (loading) {
      return (
          <div className="flex justify-center items-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
          </div>
      );
  }

  return (
    <div className="space-y-6">
      {/* En-tête */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">
          {t('header.welcome')}, {getUserDisplayName(user)}
        </h1>
        <p className="mt-1 text-sm text-gray-600">
          {t('header.adminOverview')}
        </p>
      </div>

      {/* Statistiques admin */}
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {adminStats.map((stat) => {
          const Icon = stat.icon;
          return (
            <div
              key={stat.name}
              className="bg-white overflow-hidden shadow rounded-lg"
            >
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className={`p-3 rounded-lg bg-${stat.color}-100`}>
                      <Icon className={`h-6 w-6 text-${stat.color}-600`} />
                    </div>
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">
                        {stat.name}
                      </dt>
                      <dd>
                        <div className="text-2xl font-semibold text-gray-900">
                          {stat.value}
                        </div>
                        <div
                          className={`text-sm font-medium ${
                            stat.changeType === "increase"
                              ? "text-success-600"
                              : stat.changeType === "decrease" 
                              ? "text-error-600"
                              : "text-gray-600"
                          }`}
                        >
                          {stat.change}
                        </div>
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Alertes récentes */}
        <div className="lg:col-span-1">
          <div className="bg-white shadow rounded-lg">
            <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
              <h3 className="text-lg font-medium leading-6 text-gray-900">
                {t('dashboard.alerts.title')}
              </h3>
            </div>
            <div className="divide-y divide-gray-200">
              {alerts.length === 0 ? (
                <div className="px-4 py-6 text-center text-sm text-gray-400">
                  {t('dashboard.alerts.noAlerts', 'Aucune alerte')}
                </div>
              ) : alerts.map((alert) => (
                <div key={alert.id} className="px-4 py-4 sm:px-6">
                  <div className="flex items-start">
                    <div
                      className={`flex-shrink-0 p-2 rounded-lg ${
                        alert.type === "warning"
                          ? "bg-warning-100"
                          : alert.type === "error"
                          ? "bg-error-100"
                          : "bg-primary-100"
                      }`}
                    >
                      <AlertTriangle
                        className={`h-5 w-5 ${
                          alert.type === "warning"
                            ? "text-warning-600"
                            : alert.type === "error"
                            ? "text-error-600"
                            : "text-primary-600"
                        }`}
                      />
                    </div>
                    <div className="ml-4 flex-1">
                      <p className="text-sm font-medium text-gray-900">
                        {alert.title}
                      </p>
                      <p className="text-sm text-gray-500">
                        {alert.description}
                      </p>
                      <p className="text-xs text-gray-400 mt-1">{alert.time}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Graphiques */}
        <div className="lg:col-span-2">
          <DashboardCharts />
        </div>
      </div>

      {/* Actions rapides admin */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
          <h3 className="text-lg font-medium leading-6 text-gray-900">
            {t('dashboard.quickActions.title')}
          </h3>
        </div>
        <div className="p-6 grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Link to="/admin/clients" className="flex flex-col items-center p-4 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
            <Users className="h-8 w-8 text-primary-600 mb-2" />
            <span className="text-sm font-medium text-gray-900">
              {t('dashboard.quickActions.manageClients')}
            </span>
          </Link>
          <Link to="/admin/factures" className="flex flex-col items-center p-4 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
            <FileText className="h-8 w-8 text-primary-600 mb-2" />
            <span className="text-sm font-medium text-gray-900">{t('dashboard.quickActions.invoices')}</span>
          </Link>
          <Link to="/admin/paiements" className="flex flex-col items-center p-4 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
            <CreditCard className="h-8 w-8 text-primary-600 mb-2" />
            <span className="text-sm font-medium text-gray-900">{t('dashboard.quickActions.payments')}</span>
          </Link>
          <Link to="/admin/analytics" className="flex flex-col items-center p-4 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
            <TrendingUp className="h-8 w-8 text-primary-600 mb-2" />
            <span className="text-sm font-medium text-gray-900">{t('dashboard.quickActions.reports')}</span>
          </Link>
        </div>
      </div>
    </div>
  );
};
